// src/Dashboard.jsx
import { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  addDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  doc,
} from "firebase/firestore";
import { db } from "./firebase";
import { getAuth, signOut } from "firebase/auth";
import { useTranslation } from 'react-i18next';
import FormBuilder from "./FormBuilder";
import { motion, AnimatePresence } from "framer-motion";
import logo from "./assets/image/logo_web.png";
import NewFormModal from "./components/NewFormModal";
import ViewReports from "./components/ViewReports";
import AccessManagementModal from "./components/AccessManagementModal";
import CreateUserModal from "./components/CreateUserModal";
import GroupsManagementModal from "./components/GroupsManagementModal";
import DeviceManagementModal from "./components/DeviceManagementModal";
import AssetManager from "./components/AssetManager";
import { getCurrentUserCompanyId, getCurrentUserData } from "./utils/userUtils";
import { hasPermission } from './utils/permissions';
import { buildNewDocumentControl, getDocumentControlSummary } from "./utils/documentControl";
import { checkServerStatus } from './utils/serverStatus';
import ServerStatusModal from './components/ServerStatusModal';
import { SPLIT_WORKFLOW_TYPE } from "./utils/workflowUtils";

/* ------------------------------
 * Helpers
 * ------------------------------ */
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function truncate(str = "", n = 48) {
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}
function formatTimestamp(ts) {
  if (!ts) return null;
  let dateObj;
  if (typeof ts === "string") dateObj = new Date(ts);
  else if (ts?.toDate) dateObj = ts.toDate();
  else dateObj = ts;

  const now = new Date();
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let relative;
  if (diffMins < 1) relative = "just now";
  else if (diffMins < 60) relative = `${diffMins} min${diffMins === 1 ? "" : "s"} ago`;
  else if (diffHours < 24) relative = `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  else relative = `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;

  const fullDate = dateObj.toLocaleString();
  return { fullDate, relative };
}

function xmlEscape(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function buildExcelWorksheet(name, rows) {
  const tableRows = rows
    .map(
      (row, rowIndex) => `
        <Row>
          ${row
            .map((cell) => {
              const type = typeof cell === "number" ? "Number" : "String";
              const value = cell == null ? "" : cell;
              return `<Cell${rowIndex === 0 ? ' ss:StyleID="header"' : ""}><Data ss:Type="${type}">${xmlEscape(value)}</Data></Cell>`;
            })
            .join("")}
        </Row>`
    )
    .join("");

  return `
    <Worksheet ss:Name="${xmlEscape(name)}">
      <Table>
        ${tableRows}
      </Table>
    </Worksheet>`;
}

function downloadExcelWorkbook(filename, sheets) {
  const workbook = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
  <Styles>
    <Style ss:ID="header">
      <Font ss:Bold="1"/>
      <Interior ss:Color="#DDEBF7" ss:Pattern="Solid"/>
    </Style>
  </Styles>
  ${sheets.map((sheet) => buildExcelWorksheet(sheet.name, sheet.rows)).join("")}
</Workbook>`;

  const blob = new Blob([workbook], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/* ------------------------------
 * Phone preview
 * ------------------------------ */
function PhoneRow({ text = "Field label", subtle = false }) {
  return (
    <div
      className={classNames(
        "rounded-md px-2 py-1 text-[11px] truncate text-slate-900",
        subtle
          ? "bg-white/60 dark:bg-gray-800/50 border border-gray-200/60 dark:border-gray-700/60"
          : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
      )}
      title={text}
    >
      {truncate(text, 36)}
    </div>
  );
}

function flattenSnapshotFields(fields = []) {
  const flattened = [];

  for (const field of fields) {
    if (!field) continue;

    if (field.type === SPLIT_WORKFLOW_TYPE) {
      flattened.push({
        ...field,
        label: field.label || "Split Workflow",
        __snapshotMarker: true,
      });

      if (Array.isArray(field.children) && field.children.length > 0) {
        flattened.push(...flattenSnapshotFields(field.children));
      }
      continue;
    }

    flattened.push(field);
  }

  return flattened;
}

function PhonePreview({ form }) {
  const fields = flattenSnapshotFields(Array.isArray(form?.fields) ? form.fields : []);
  const previewFields = fields.slice(0, 4);
  return (
    <div className="relative w-full text-slate-900">
      <div className="relative mx-auto aspect-[9/19] w-full rounded-[22px] border-[6px] border-gray-300 dark:border-gray-700 shadow-[inset_0_2px_10px_rgba(0,0,0,0.25)] overflow-hidden bg-gray-100 dark:bg-gray-900">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-20 bg-gray-800/90 rounded-b-xl mt-0.5" />
        <div className="absolute top-4 left-0 right-0 text-center">
          <div className="inline-block bg-rose-700 text-white text-[10px] font-semibold px-2 py-0.5 rounded">
            {truncate(form?.title || "Untitled Form", 28)}
          </div>
        </div>
        <div className="absolute inset-0 pt-10 px-3 pb-3 flex flex-col gap-1.5">
          {previewFields.length === 0 ? (
            <>
              <PhoneRow text="(empty form)" />
              <PhoneRow text="Add fields in builder" subtle />
              <PhoneRow text="…" subtle />
            </>
          ) : (
            previewFields.map((f, i) => (
              <PhoneRow
                key={i}
                text={`${f.label || "Question"} ${f.required ? "• required" : ""}`}
                subtle={i % 2 === 1}
              />
            ))
          )}
          {fields.length > previewFields.length && (
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
              +{fields.length - previewFields.length} more…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------
 * Card actions
 * ------------------------------ */
function CardActions({
  onEdit,
  onPreview,
  onViewAuditTrail,
  onViewReports,
  onDuplicate,
  onDelete,
  onArchive,
  onUnarchive,
  onManageAccess,
  archived,
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 border border-white/10 rounded-xl bg-white/5 backdrop-blur-md shadow-inner shadow-black/30 p-3 text-white">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60 mb-2">
        {t('common.actions')}
      </div>

      {!archived ? (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onEdit}
            className="flex items-center justify-center gap-1 text-sm font-semibold rounded-md py-2 transition-all bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 text-white shadow-lg shadow-fuchsia-900/30"
          >
            {t('dashboard.editAction')}
          </button>
          <button
            onClick={onPreview}
            className="flex items-center justify-center gap-1 text-sm font-medium rounded-md py-2 transition-all border border-white/15 bg-white/10 text-white/80 hover:bg-white/20"
          >
            {t('dashboard.previewAction')}
          </button>
          <button
            onClick={onViewReports}
            className="flex items-center justify-center gap-1 text-sm font-semibold rounded-md py-2 transition-all bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-900/30"
          >
            {t('dashboard.reportsAction')}
          </button>
          <button
            onClick={onViewAuditTrail}
            className="flex items-center justify-center gap-1 text-sm font-medium rounded-md py-2 transition-all border border-cyan-300/40 bg-cyan-500/15 text-white hover:bg-cyan-500/25"
          >
            {t('dashboard.auditTrailAction')}
          </button>
          <button
            onClick={onManageAccess}
            className="flex items-center justify-center gap-1 text-sm font-medium rounded-md py-2 transition-all border border-purple-300/40 bg-purple-500/20 text-white hover:bg-purple-500/30"
          >
            {t('dashboard.accessAction')}
          </button>
          <button
            onClick={onDuplicate}
            className="flex items-center justify-center gap-1 text-sm font-medium rounded-md py-2 transition-all border border-blue-200/40 bg-blue-500/15 text-white hover:bg-blue-500/25"
          >
            {t('dashboard.duplicateAction')}
          </button>
          <button
            onClick={onArchive}
            className="flex items-center justify-center gap-1 text-sm font-semibold rounded-md py-2 transition-all border border-amber-300/50 bg-amber-500/15 text-amber-50 hover:bg-amber-500/25"
          >
            {t('dashboard.archiveAction')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onUnarchive}
            className="flex items-center justify-center gap-1 text-sm font-semibold rounded-md py-2 transition-all bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-900/30"
          >
            {t('dashboard.restoreAction')}
          </button>
          <button
            onClick={onDelete}
            className="flex items-center justify-center gap-1 text-sm font-semibold rounded-md py-2 transition-all border border-rose-300/50 bg-rose-500/15 text-rose-50 hover:bg-rose-500/25"
          >
            {t('dashboard.deleteAction')}
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------
 * Dashboard
 * ------------------------------ */
export default function Dashboard() {
  const { t } = useTranslation();
  const [forms, setForms] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [viewReportsForm, setViewReportsForm] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [auditTrailForm, setAuditTrailForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [manageAccessForm, setManageAccessForm] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("bmc:viewMode") || "phone"
  );
  const [compactGrid, setCompactGrid] = useState(
    () => localStorage.getItem("bmc:compactGrid") === "1"
  );
  const [showNewFormModal, setShowNewFormModal] = useState(false);
  const [uploadedFormData, setUploadedFormData] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showManageGroups, setShowManageGroups] = useState(false);
  const [showManageDevices, setShowManageDevices] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [currentView, setCurrentView] = useState("forms"); // "forms" or "assets"
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderViewMode, setFolderViewMode] = useState(
    () => localStorage.getItem("bmc:folderView") === "1"
  );

  const translateMobileEditLabel = (label) => {
    if (label === "Web + mobile") return t('dashboard.documentControl.webAndMobile');
    if (label === "Web only") return t('dashboard.documentControl.webOnly');
    return label || t('dashboard.documentControl.unknown');
  };

  const translatePlatformLabel = (platform) => {
    if (platform === "web") return t('dashboard.documentControl.platformWeb');
    if (platform === "mobile") return t('dashboard.documentControl.platformMobile');
    return platform || t('dashboard.documentControl.unknown');
  };

  // Load current user profile for permission checks
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const ud = await getCurrentUserData();
        if (!mounted) return;
        setCurrentUserData(ud || null);
        // also make it globally available for simple checks elsewhere
        try { window.__currentUserData = ud || null; } catch (e) {}
      } catch (e) {
        console.error('Failed to load current user data', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const [serverStatus, setServerStatus] = useState({ ok: null, checks: null, durationMs: null, lastChecked: null });
  const [showServerModal, setShowServerModal] = useState(false);
  useEffect(() => {
    let mounted = true;
    async function check() {
      try {
        const res = await checkServerStatus();
        if (!mounted) return;
        setServerStatus({ ok: res.ok, checks: res.checks, durationMs: res.durationMs, lastChecked: new Date() });
      } catch (e) {
        if (!mounted) return;
        setServerStatus({ ok: false, checks: null, durationMs: null, lastChecked: new Date(), error: e?.message || String(e) });
      }
    }
    check();
    const iv = setInterval(check, 30000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  // Theme is controlled globally in App; per-page toggle removed.
  const toggleViewMode = () => {
    const next = viewMode === "phone" ? "list" : "phone";
    setViewMode(next);
    localStorage.setItem("bmc:viewMode", next);
  };
  const toggleCompactGrid = () => {
    const next = !compactGrid;
    setCompactGrid(next);
    localStorage.setItem("bmc:compactGrid", next ? "1" : "0");
  };
  const toggleFolderView = () => {
    const next = !folderViewMode;
    setFolderViewMode(next);
    localStorage.setItem("bmc:folderView", next ? "1" : "0");
  };
  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      window.location.href = "/login";
    } catch (err) {
      alert("Logout failed: " + err.message);
    }
  };

  // Fetch company ID and set up Firestore listener
  useEffect(() => {
    const loadCompanyAndForms = async () => {
      const userCompanyId = await getCurrentUserCompanyId();
      if (!userCompanyId) {
        setError("No company assigned to your account");
        setLoading(false);
        return;
      }
      setCompanyId(userCompanyId);

      const formsRef = collection(db, `companies/${userCompanyId}/forms`);
      const formsQuery = query(formsRef, orderBy("updatedAt", "desc"));
      const foldersRef = collection(db, `companies/${userCompanyId}/folders`);
      const foldersQuery = query(foldersRef, orderBy("name", "asc"));
      
      const unsubscribeForms = onSnapshot(
        formsQuery,
        (snapshot) => {
          const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          setForms(list);
          setLoading(false);
        },
        (err) => {
          console.error("Forms realtime error:", err);
          setError(err.message || "Forms realtime error");
          setLoading(false);
        }
      );
      
      const unsubscribeFolders = onSnapshot(
        foldersQuery,
        (snapshot) => {
          const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          setFolders(list);
        },
        (err) => {
          console.error("Folders realtime error:", err);
        }
      );
      
      return () => {
        unsubscribeForms();
        unsubscribeFolders();
      };
    };
    loadCompanyAndForms();
  }, []);

  const activeForms = useMemo(() => {
    const filtered = forms.filter((f) => !f.archived);
    if (selectedFolder === null) {
      return filtered.filter(f => !f.folderId); // Show only forms not in folders
    }
    return filtered.filter(f => f.folderId === selectedFolder);
  }, [forms, selectedFolder]);
  
  const archivedForms = useMemo(() => {
    const filtered = forms.filter((f) => f.archived);
    if (selectedFolder === null) {
      return filtered.filter(f => !f.folderId); // Show only forms not in folders
    }
    return filtered.filter(f => f.folderId === selectedFolder);
  }, [forms, selectedFolder]);
  
  const folderCounts = useMemo(() => {
    const counts = {};
    folders.forEach(folder => {
      counts[folder.id] = {
        active: forms.filter(f => f.folderId === folder.id && !f.archived).length,
        archived: forms.filter(f => f.folderId === folder.id && f.archived).length
      };
    });
    counts.unfiled = {
      active: forms.filter(f => !f.folderId && !f.archived).length,
      archived: forms.filter(f => !f.folderId && f.archived).length
    };
    return counts;
  }, [forms, folders]);

  async function handleArchive(form, value) {
    if (!companyId) return;
    setForms((p) => p.map((f) => (f.id === form.id ? { ...f, archived: value } : f)));
    try {
      await setDoc(
        doc(db, `companies/${companyId}/forms/${form.id}`),
        { archived: value, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error("Error archiving:", err);
      alert(t('dashboard.failedUpdateArchive'));
    }
  }
  function handleDelete(form) {
    setConfirmDelete(form);
  }
  async function confirmDeleteForm(form) {
    if (!companyId) return;
    try {
      await deleteDoc(doc(db, `companies/${companyId}/forms`, form.id));
      setConfirmDelete(null);
    } catch (err) {
      console.error("Error deleting:", err);
      alert(t('dashboard.failedDeleteForm'));
    }
  }
  async function handleDuplicate(form) {
    if (!companyId) return;
    try {
      const currentUser = await getCurrentUserData();
      const payload = {
        title: `${form.title || "Untitled"} (copy)`,
        fields: Array.isArray(form.fields) ? form.fields : [],
        folderId: form.folderId || null,
        updatedAt: new Date().toISOString(),
        archived: false,
        createdAt: serverTimestamp(),
        createdVia: "web",
        lastEditedVia: "web",
        allowMobileEdit: false,
        source: "web",
        createdBy: currentUser?.uid || "unknown",
        createdByName: currentUser?.fullName || currentUser?.email || "Unknown user",
        ...buildNewDocumentControl({
          user: {
            uid: currentUser?.uid || "unknown",
            fullName: currentUser?.fullName || null,
            email: currentUser?.email || null,
          },
          platform: "web",
          summary: "Duplicated from existing form",
          comments: "",
        }),
      };
      await addDoc(collection(db, `companies/${companyId}/forms`), payload);
    } catch (err) {
      console.error("Error duplicating:", err);
      alert(t('dashboard.failedDuplicateForm'));
    }
  }

  async function createFolder() {
    if (!companyId || !newFolderName.trim()) return;
    try {
      await addDoc(collection(db, `companies/${companyId}/folders`), {
        name: newFolderName.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewFolderName("");
      setShowCreateFolder(false);
    } catch (err) {
      console.error("Error creating folder:", err);
      alert(t('dashboard.failedCreateFolder'));
    }
  }

  async function deleteFolder(folderId) {
    if (!companyId) return;
    
    // Move forms out of folder first
    const formsInFolder = forms.filter(f => f.folderId === folderId);
    try {
      // Update all forms in folder to remove folderId
      await Promise.all(
        formsInFolder.map(form => 
          setDoc(
            doc(db, `companies/${companyId}/forms/${form.id}`),
            { folderId: null, updatedAt: serverTimestamp() },
            { merge: true }
          )
        )
      );
      
      // Delete the folder
      await deleteDoc(doc(db, `companies/${companyId}/folders`, folderId));
      
      // Reset selected folder if it was deleted
      if (selectedFolder === folderId) {
        setSelectedFolder(null);
      }
    } catch (err) {
      console.error("Error deleting folder:", err);
      alert(t('dashboard.failedDeleteFolder'));
    }
  }

  async function updateFolderName(folderId, newName) {
    if (!companyId || !newName.trim()) return;
    try {
      await setDoc(
        doc(db, `companies/${companyId}/folders/${folderId}`),
        { name: newName.trim(), updatedAt: serverTimestamp() },
        { merge: true }
      );
      setEditingFolder(null);
    } catch (err) {
      console.error("Error updating folder:", err);
      alert(t('dashboard.failedUpdateFolder'));
    }
  }

  async function moveFormToFolder(formId, folderId) {
    if (!companyId) return;
    try {
      await setDoc(
        doc(db, `companies/${companyId}/forms/${formId}`),
        { folderId: folderId, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error("Error moving form:", err);
      alert(t('dashboard.failedMoveForm'));
    }
  }

  function exportDocumentControlWorkbook() {
    const allForms = [...forms].sort((a, b) => {
      const aTitle = (a.title || "").toLowerCase();
      const bTitle = (b.title || "").toLowerCase();
      return aTitle.localeCompare(bTitle);
    });

    const mdlRows = [
      [
        t('dashboard.documentControl.formTitle'),
        t('dashboard.documentControl.status'),
        t('dashboard.documentControl.documentType'),
        t('dashboard.documentControl.currentRevision'),
        t('dashboard.documentControl.author'),
        t('dashboard.documentControl.approvalDate'),
        t('dashboard.documentControl.latestChange'),
        t('dashboard.documentControl.comments'),
        t('dashboard.documentControl.mobileEditability'),
        t('dashboard.documentControl.createdVia'),
        t('dashboard.documentControl.lastEditedVia'),
        t('dashboard.documentControl.folder'),
      ],
      ...allForms.map((form) => {
        const docControl = getDocumentControlSummary(form);
        const folderName = folders.find((folder) => folder.id === form.folderId)?.name || t('dashboard.unfiled');
        return [
          form.title || t('dashboard.untitledForm'),
          form.archived ? t('dashboard.documentControl.archived') : t('dashboard.documentControl.active'),
          docControl.documentType,
          docControl.revisionLabel,
          docControl.authorName,
          docControl.approvedAt ? docControl.approvedAt.toLocaleString() : "",
          docControl.lastChangeSummary,
          docControl.lastComments,
          translateMobileEditLabel(docControl.mobileEditLabel),
          translatePlatformLabel(form.createdVia),
          translatePlatformLabel(form.lastEditedVia),
          folderName,
        ];
      }),
    ];

    const revisionRows = [
      [
        t('dashboard.documentControl.formTitle'),
        t('dashboard.documentControl.revision'),
        t('dashboard.documentControl.changedBy'),
        t('dashboard.documentControl.platform'),
        t('dashboard.documentControl.updatedAt'),
        t('dashboard.documentControl.summary'),
        t('dashboard.documentControl.comments'),
      ],
      ...allForms.flatMap((form) => {
        const history = getDocumentControlSummary(form).history;
        if (!history.length) {
          return [[form.title || t('dashboard.untitledForm'), "Rev 1", "", "", "", t('dashboard.documentControl.noRevisionHistoryRecorded'), ""]];
        }
        return history.map((entry) => [
          form.title || t('dashboard.untitledForm'),
          entry.revisionLabel || `Rev ${entry.revisionNumber || ""}`,
          entry.changedByName || "",
          translatePlatformLabel(entry.platform),
          entry.updatedAt ? new Date(entry.updatedAt).toLocaleString() : "",
          entry.changeSummary || "",
          entry.comments || "",
        ]);
      }),
    ];

    const summaryRows = [
      [t('dashboard.documentControl.metric'), t('dashboard.documentControl.value')],
      [t('dashboard.documentControl.totalForms'), allForms.length],
      [t('dashboard.documentControl.activeForms'), allForms.filter((form) => !form.archived).length],
      [t('dashboard.documentControl.archivedForms'), allForms.filter((form) => form.archived).length],
      [t('dashboard.documentControl.webOnlyForms'), allForms.filter((form) => getDocumentControlSummary(form).mobileEditLabel === "Web only").length],
      [t('dashboard.documentControl.webAndMobileForms'), allForms.filter((form) => getDocumentControlSummary(form).mobileEditLabel === "Web + mobile").length],
      [t('dashboard.documentControl.totalRevisionEntries'), allForms.reduce((count, form) => count + getDocumentControlSummary(form).history.length, 0)],
      [t('dashboard.documentControl.exportedAt'), new Date().toLocaleString()],
    ];

    downloadExcelWorkbook(`buildmychecks-document-control-${new Date().toISOString().slice(0, 10)}.xls`, [
      { name: t('dashboard.documentControl.masterDocumentListSheet'), rows: mdlRows },
      { name: t('dashboard.documentControl.revisionHistorySheet'), rows: revisionRows },
      { name: t('dashboard.documentControl.formsSummarySheet'), rows: summaryRows },
    ]);
  }

  return (
    <AnimatePresence mode="wait">
      {creating ? (
        <FormBuilder
          formId={null}
          initialFormData={uploadedFormData}
          onBack={() => {
            setCreating(false);
            setUploadedFormData(null);
          }}
        />
      ) : editingForm ? (
        <FormBuilder formId={editingForm.id} onBack={() => setEditingForm(null)} />
      ) : viewReportsForm ? (
        <ViewReports
          form={viewReportsForm}
          onBack={() => setViewReportsForm(null)}
        />
      ) : (
        <motion.div
          key="dashboard"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white/90 font-sans"
        >
          {/* Header */}
          <header className="sticky top-0 z-30 flex flex-wrap justify-between items-center px-6 py-4 bg-white/10 backdrop-blur-xl border-b border-white/10 shadow-lg gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                <img src={logo} alt="BuildMyChecks" className="w-8 h-8" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/60">BuildMyChecks</p>
                <h2 className="text-xl font-extrabold text-white">{t('nav.dashboard')}</h2>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => window.location.href = "https://buildmychecks.co.uk/home"}
                className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/15 transition-all"
              >
                {t('dashboardHeader.home')}
              </button>
              <button
                onClick={toggleFolderView}
                className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/15 text-sm"
              >
                {folderViewMode ? t('dashboardHeader.hideFolders') : t('dashboardHeader.showFolders')}
              </button>
              <button
                onClick={toggleCompactGrid}
                className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/15 text-sm"
              >
                {compactGrid ? t('dashboardHeader.normalGrid') : t('dashboardHeader.compactGrid')}
              </button>

              {hasPermission(currentUserData, 'create_forms') ? (
                <>
                  <button
                    onClick={() => (window.location.href = "/preset-library")}
                    className="px-3 py-2 rounded-full border border-cyan-300/30 bg-cyan-500/15 text-cyan-50 hover:bg-cyan-500/25 text-sm font-medium"
                  >
                    AI Presets
                  </button>
                  <button
                    onClick={() => setShowNewFormModal(true)}
                    className="px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 text-white font-semibold shadow-md hover:shadow-xl"
                  >
                    {t('dashboardHeader.createForm')}
                  </button>
                </>
              ) : null}

              <button
                onClick={exportDocumentControlWorkbook}
                className="px-3 py-2 rounded-full border border-cyan-300/40 bg-cyan-500/15 text-cyan-50 hover:bg-cyan-500/25 text-sm font-medium"
              >
                Export MDL
              </button>

              <button
                onClick={() => window.location.href = "/conduct"}
                className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/15"
              >
                {t('dashboardHeader.conduct')}
              </button>

              <button
                onClick={() => window.location.href = "/manage-roles"}
                className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/15"
              >
                {t('dashboardHeader.manageUsers')}
              </button>

              <button
                onClick={() => setShowManageGroups(true)}
                className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/15"
              >
                {t('dashboardHeader.groups')}
              </button>

              <button
                onClick={() => setShowManageDevices(true)}
                className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/15"
              >
                {t('dashboardHeader.devices')}
              </button>

              <button
                onClick={() => window.location.href = "/subscription"}
                className="px-3 py-2 rounded-full border border-emerald-400/30 bg-emerald-500/20 text-emerald-50 hover:bg-emerald-500/30"
              >
                {t('dashboardHeader.billing')}
              </button>

              <div className="flex border border-white/15 rounded-full overflow-hidden bg-white/5">
                <button
                  onClick={() => setActiveTab("active")}
                  className={classNames(
                    "px-3 py-2 text-sm font-medium",
                    activeTab === "active"
                      ? "bg-white/20 text-white"
                      : "text-white/70 hover:bg-white/10"
                  )}
                >
                  Active
                </button>
                <button
                  onClick={() => setActiveTab("archived")}
                  className={classNames(
                    "px-3 py-2 text-sm font-medium",
                    activeTab === "archived"
                      ? "bg-white/20 text-white"
                      : "text-white/70 hover:bg-white/10"
                  )}
                >
                  {t('dashboardHeader.archived')}
                </button>
              </div>

              <button
                onClick={toggleViewMode}
                className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/15 text-sm"
              >
                {viewMode === "phone" ? t('dashboardHeader.listView') : t('dashboardHeader.phoneView')}
              </button>

              <button
                onClick={() => window.dispatchEvent(new Event("logout"))}
                className="px-3 py-2 rounded-full border border-rose-400/50 text-rose-50 bg-rose-500/20 hover:bg-rose-500/30 text-sm"
              >
                {t('dashboardHeader.logout')}
              </button>

              <div className="flex items-center gap-2 ml-2">
                <button onClick={() => setShowServerModal(true)}
                  title={serverStatus.ok ? `${t('dashboardHeader.serverOk')} (checked ${serverStatus.lastChecked ? serverStatus.lastChecked.toLocaleTimeString() : ''})` : (serverStatus.error || t('common.checking'))}
                  className={`px-3 py-2 rounded-full text-sm font-medium border ${serverStatus.ok ? 'border-emerald-300/50 bg-emerald-500/15 text-emerald-50' : (serverStatus.ok === false ? 'border-rose-300/50 bg-rose-500/15 text-rose-50' : 'border-white/20 bg-white/5 text-white/70')}`}
                >
                  {serverStatus.ok ? t('dashboardHeader.serverOk') : (serverStatus.ok === false ? t('dashboardHeader.serverUnreachable') : t('common.checking'))}
                </button>
                <button onClick={async () => {
                  try {
                    const res = await checkServerStatus();
                    setServerStatus({ ok: res.ok, checks: res.checks, durationMs: res.durationMs, lastChecked: new Date() });
                  } catch (e) {
                    setServerStatus({ ok: false, checks: null, durationMs: null, lastChecked: new Date(), error: e?.message || String(e) });
                  }
                }} className="px-2 py-2 rounded-full border border-white/15 bg-white/5 text-sm text-white/80 hover:bg-white/15">⟳</button>
              </div>
              <ServerStatusModal open={showServerModal} onClose={() => setShowServerModal(false)} status={serverStatus} />
            </div>
          </header>

          {/* Tab Navigation */}
          <div className="px-6 pt-6">
            <nav className="flex gap-2 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-2 text-white">
              <button
                onClick={() => setCurrentView("forms")}
                className={classNames(
                  "px-4 py-2 rounded-xl text-sm font-semibold transition-all",
                  currentView === "forms"
                    ? "bg-white/20 text-white shadow"
                    : "text-white/70 hover:bg-white/10"
                )}
              >
                {t('dashboardHeader.forms')}
              </button>
              <button
                onClick={() => setCurrentView("assets")}
                className={classNames(
                  "px-4 py-2 rounded-xl text-sm font-semibold transition-all",
                  currentView === "assets"
                    ? "bg-white/20 text-white shadow"
                    : "text-white/70 hover:bg-white/10"
                )}
              >
                {t('dashboardHeader.assets')}
              </button>
            </nav>
          </div>

          {/* Main */}
          <main className={folderViewMode && currentView === "forms" ? "flex" : currentView === "assets" ? "" : "px-6 py-6"}>
            {/* Folder Sidebar */}
            {folderViewMode && (
              <div className="w-64 border-r border-white/10 bg-white/5 backdrop-blur-xl p-4 space-y-2 text-white/90">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">{t('dashboard.folders')}</h3>
                  <button
                    onClick={() => setShowCreateFolder(true)}
                    className="bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white px-2 py-1 rounded text-xs shadow"
                    title="Create New Folder"
                  >
                    ➕
                  </button>
                </div>
                
                {/* Unfiled Forms */}
                <div
                  onClick={() => setSelectedFolder(null)}
                  className={classNames(
                    "flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors border border-transparent",
                    selectedFolder === null
                      ? "bg-white/15 text-white border-white/20"
                      : "hover:bg-white/10 text-white/80"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span>📋</span>
                    <span className="text-sm font-medium">{t('dashboard.unfiled')}</span>
                  </div>
                  <span className="text-xs text-white/60">
                    {activeTab === "active" ? folderCounts.unfiled?.active || 0 : folderCounts.unfiled?.archived || 0}
                  </span>
                </div>
                
                {/* Folder List */}
                {folders.map(folder => (
                  <div key={folder.id} className="group">
                    <div
                      className={classNames(
                        "flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors border border-transparent",
                        selectedFolder === folder.id
                          ? "bg-white/15 text-white border-white/20"
                          : "hover:bg-white/10 text-white/80"
                      )}
                    >
                      <div 
                        onClick={() => setSelectedFolder(folder.id)}
                        className="flex items-center gap-2 flex-1"
                      >
                        <span>📁</span>
                        {editingFolder === folder.id ? (
                          <input
                            type="text"
                            defaultValue={folder.name}
                            className="text-sm bg-white/5 border border-white/20 rounded px-1 py-0.5 flex-1 text-white"
                            onBlur={(e) => updateFolderName(folder.id, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') updateFolderName(folder.id, e.target.value);
                              if (e.key === 'Escape') setEditingFolder(null);
                            }}
                            autoFocus
                          />
                        ) : (
                          <span className="text-sm font-medium truncate">{folder.name}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-white/60">
                          {activeTab === "active" ? folderCounts[folder.id]?.active || 0 : folderCounts[folder.id]?.archived || 0}
                        </span>
                        <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingFolder(folder.id);
                            }}
                            className="text-xs text-white/50 hover:text-white"
                            title={t('dashboard.renameFolder')}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete folder "${folder.name}"? Forms in this folder will be moved to unfiled.`)) {
                                deleteFolder(folder.id);
                              }
                            }}
                            className="text-xs text-white/50 hover:text-red-200"
                            title={t('dashboard.deleteFolderTitle')}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Create Folder Form */}
                {showCreateFolder && (
                  <div className="p-2 border border-white/20 rounded-lg bg-white/5">
                    <input
                      type="text"
                      placeholder={t('dashboard.folderNamePlaceholder')}
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      className="w-full text-sm bg-white/5 border border-white/20 rounded px-2 py-1 mb-2 text-white"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') createFolder();
                        if (e.key === 'Escape') {
                          setShowCreateFolder(false);
                          setNewFolderName("");
                        }
                      }}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={createFolder}
                        className="flex-1 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white px-2 py-1 rounded text-xs"
                      >
                        {t('dashboard.createNewFolder')}
                      </button>
                      <button
                        onClick={() => {
                          setShowCreateFolder(false);
                          setNewFolderName("");
                        }}
                        className="flex-1 border border-white/20 px-2 py-1 rounded text-xs text-white/80 hover:bg-white/10"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Content Area */}
            <div className={folderViewMode && currentView === "forms" ? "flex-1 p-6" : currentView === "assets" ? "h-[calc(100vh-120px)]" : ""}>
            {currentView === "forms" ? (
              <>
            {/* Current Folder Header */}
            {folderViewMode && (
              <div className="mb-4 pb-3 border-b border-white/10 text-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">
                    {selectedFolder === null ? (
                      <>📋 Unfiled Forms</>
                    ) : (
                      <>📁 {folders.find(f => f.id === selectedFolder)?.name || 'Unknown Folder'}</>
                    )}
                  </h2>
                  <span className="text-sm text-white/60">
                    {activeTab === "active" ? activeForms.length : archivedForms.length} forms
                  </span>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="grid place-items-center h-[50vh] text-white/70">
                {t('common.loading')}
              </div>
            ) : error ? (
              <p className="text-center text-rose-200">Error: {error}</p>
            ) : activeTab === "archived" ? (
              <FormList
                forms={archivedForms}
                folders={folders}
                archived
                viewMode={viewMode}
                compactGrid={compactGrid}
                onEdit={setEditingForm}
                onPreview={setPreviewForm}
                onViewAuditTrail={setAuditTrailForm}
                onViewReports={setViewReportsForm}
                onDuplicate={handleDuplicate}
                onUnarchive={(f) => handleArchive(f, false)}
                onDelete={handleDelete}
                onPreviewAttachment={setPreviewAttachment}
                onMoveToFolder={moveFormToFolder}
                onManageAccess={setManageAccessForm}
                showFolderActions={folderViewMode}
              />
            ) : (
              <FormList
                forms={activeForms}
                folders={folders}
                viewMode={viewMode}
                compactGrid={compactGrid}
                onEdit={setEditingForm}
                onPreview={setPreviewForm}
                onViewAuditTrail={setAuditTrailForm}
                onViewReports={setViewReportsForm}
                onDuplicate={handleDuplicate}
                onArchive={(f) => handleArchive(f, true)}
                onPreviewAttachment={setPreviewAttachment}
                onMoveToFolder={moveFormToFolder}
                onManageAccess={setManageAccessForm}
                showFolderActions={folderViewMode}
              />
            )}
            </>
            ) : (
              /* Assets View */
              <AssetManager companyId={companyId} />
            )}
            </div>
          </main>

          {/* Form Preview Modal */}
          <AnimatePresence>
            {previewForm && (
              <motion.div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="bg-white dark:bg-gray-900 p-5 rounded-xl shadow-2xl max-w-xs sm:max-w-sm w-full border border-gray-200 dark:border-gray-800 relative">
                  <button
                    onClick={() => setPreviewForm(null)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
                  >
                    ✕
                  </button>
                  <h3 className="text-sm font-semibold mb-3 text-gray-800 dark:text-gray-100 text-center">
                    {previewForm.title || t('dashboard.untitledForm')}
                  </h3>
                  <div className="mx-auto w-[200px] sm:w-[230px] scale-[0.85] origin-top drop-shadow-lg">
                    <PhonePreview form={previewForm} />
                  </div>
                  <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400 text-center">
                    ({t('dashboard.visualPreviewOnly')})
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {auditTrailForm && (
              <motion.div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="relative w-full max-w-3xl rounded-2xl border border-white/10 bg-slate-950/95 p-6 text-white shadow-2xl">
                  <button
                    onClick={() => setAuditTrailForm(null)}
                    className="absolute top-3 right-3 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-sm text-white/80 hover:bg-white/10"
                  >
                    {t('common.close')}
                  </button>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {auditTrailForm.title || t('dashboard.untitledForm')}
                  </h3>
                  <p className="text-sm text-white/60 mb-4">
                    {t('dashboard.documentControl.auditTrailDescription')}
                  </p>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-4">
                    {(() => {
                      const docControl = getDocumentControlSummary(auditTrailForm);
                      return (
                        <div className="grid gap-2 md:grid-cols-2 text-sm text-white/80">
                          <div>{t('dashboard.documentControl.currentRevision')}: <span className="font-semibold text-white">{docControl.revisionLabel}</span></div>
                          <div>{t('dashboard.documentControl.mobileEditing')}: <span className="font-semibold text-white">{translateMobileEditLabel(docControl.mobileEditLabel)}</span></div>
                          <div>{t('dashboard.documentControl.documentType')}: <span className="font-semibold text-white">{docControl.documentType}</span></div>
                          <div>{t('dashboard.documentControl.author')}: <span className="font-semibold text-white">{docControl.authorName}</span></div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    {getDocumentControlSummary(auditTrailForm).history.length > 0 ? (
                      getDocumentControlSummary(auditTrailForm).history
                        .slice()
                        .reverse()
                        .map((entry, index) => (
                          <div key={`${entry.revisionNumber}-${index}`} className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <div className="text-sm font-semibold text-white">{entry.revisionLabel}</div>
                              <div className="text-xs uppercase tracking-wider text-cyan-200/80">{translatePlatformLabel(entry.platform)}</div>
                            </div>
                            <div className="grid gap-1 text-sm text-white/75">
                              <div>{t('dashboard.documentControl.changedBy')}: <span className="text-white">{entry.changedByName || t('dashboard.documentControl.unknownUser')}</span></div>
                              <div>{t('dashboard.documentControl.date')}: <span className="text-white">{entry.updatedAt ? new Date(entry.updatedAt).toLocaleString() : t('dashboard.documentControl.notRecorded')}</span></div>
                              <div>{t('dashboard.documentControl.summary')}: <span className="text-white">{entry.changeSummary || t('dashboard.documentControl.noSummaryProvided')}</span></div>
                              {entry.comments ? (
                                <div>{t('dashboard.documentControl.comments')}: <span className="text-white">{entry.comments}</span></div>
                              ) : null}
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
                        {t('dashboard.documentControl.noRevisionHistoryYet')}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Attachment Preview Modal */}
          <AnimatePresence>
            {previewAttachment && (
              <motion.div
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-lg max-w-3xl w-full overflow-hidden">
                  <button
                    onClick={() => setPreviewAttachment(null)}
                    className="absolute top-2 right-2 bg-black/60 text-white rounded-full px-2 py-0.5 text-sm"
                  >
                    ✕
                  </button>
                  <a
                    href={previewAttachment.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-2 left-2 bg-rose-700 hover:bg-rose-800 text-white rounded-md px-2 py-1 text-xs font-medium shadow-sm"
                  >
                    📥 {t('dashboard.download')}
                  </a>

                  {previewAttachment.type?.startsWith("image/") ? (
                    <img
                      src={previewAttachment.url}
                      alt={previewAttachment.name}
                      className="w-full max-h-[80vh] object-contain"
                    />
                  ) : (
                    <iframe
                      src={previewAttachment.url}
                      title={previewAttachment.name}
                      className="w-full h-[80vh]"
                    />
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Delete Modal */}
          <AnimatePresence>
            {confirmDelete && (
              <motion.div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-sm w-full border border-gray-300 dark:border-gray-700">
                  <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
                    {t('dashboard.confirmDelete')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    {t('dashboard.confirmDeleteMessage', { title: confirmDelete.title || t('dashboard.untitledForm') })}
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="border border-gray-300 dark:border-gray-700 px-3 py-1.5 rounded-md text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={() => confirmDeleteForm(confirmDelete)}
                      className="bg-rose-700 hover:bg-rose-800 text-white px-3 py-1.5 rounded-md text-sm"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* New Form Modal */}
          <NewFormModal
            isOpen={showNewFormModal}
            onClose={() => setShowNewFormModal(false)}
            onScratch={() => {
              setUploadedFormData(null);
              setCreating(true);
              setShowNewFormModal(false);
            }}
            onDocumentUpload={(formData) => {
              setUploadedFormData(formData);
              setCreating(true);
              setShowNewFormModal(false);
            }}
          />

          {/* Access Management Modal */}
          {manageAccessForm && (
            <AccessManagementModal
              form={manageAccessForm}
              companyId={companyId}
              onClose={() => setManageAccessForm(null)}
              onSave={(updatedForm) => {
                // Update the local form in state to reflect changes
                setForms(prevForms => 
                  prevForms.map(form => 
                    form.id === updatedForm.id ? updatedForm : form
                  )
                );
              }}
            />
          )}

          {/* Create User Modal */}
          {showCreateUser && (
            <CreateUserModal
              companyId={companyId}
              onClose={() => setShowCreateUser(false)}
              onUserCreated={(newUser) => {
                console.log('User created:', newUser);
                // You can add a success notification here
              }}
            />
          )}

          {/* Groups Management Modal */}
          {showManageGroups && (
            <GroupsManagementModal
              companyId={companyId}
              onClose={() => setShowManageGroups(false)}
            />
          )}

          {/* Device Management Modal */}
          {showManageDevices && (
            <DeviceManagementModal
              onClose={() => setShowManageDevices(false)}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ------------------------------
 * FormList
 * ------------------------------ */
function FormList({
  forms,
  folders = [],
  archived = false,
  onEdit,
  onPreview,
  onViewAuditTrail,
  onViewReports,
  onDuplicate,
  onArchive,
  onUnarchive,
  onDelete,
  onPreviewAttachment,
  onMoveToFolder,
  onManageAccess,
  viewMode = "phone",
  compactGrid = false,
  showFolderActions = false,
}) {
  const { t } = useTranslation();

  const translateMobileEditLabel = (label) => {
    if (label === "Web + mobile") return t('dashboard.documentControl.webAndMobile');
    if (label === "Web only") return t('dashboard.documentControl.webOnly');
    return label || t('dashboard.documentControl.unknown');
  };

  if (forms.length === 0)
    return (
      <div className="text-center py-20 text-white/70">
        {archived ? t('dashboard.noArchivedForms') : t('dashboard.noActiveForms')}
      </div>
    );

  // Compact grid uses more columns & tighter spacing
  const gridCols = compactGrid
    ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-4"
    : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6";

  return (
    <motion.div layout className={classNames("grid", gridCols)}>
      {forms.map((form) => {
        const fields = flattenSnapshotFields(Array.isArray(form.fields) ? form.fields : []);
        const docControl = getDocumentControlSummary(form);
        const attachmentsByField = fields
          .filter((f) => !f.__snapshotMarker && Array.isArray(f.attachments) && f.attachments.length > 0)
          .map((f) => ({ label: f.label || "Untitled field", attachments: f.attachments }));

        return (
          <motion.div
            key={form.id}
            layout
            className={classNames(
              "rounded-2xl p-4 border shadow-lg hover:shadow-xl transition-all backdrop-blur-xl text-white",
              compactGrid ? "text-[13px]" : "text-[14px]",
              archived
                ? "border-amber-200/40 bg-amber-500/10 shadow-amber-900/20"
                : "border-white/10 bg-white/5 shadow-black/30"
            )}
          >
            {/* Header row */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className={classNames("font-semibold text-white", compactGrid ? "text-sm" : "text-base")}>
                  {form.title || "Untitled Form"}
                </div>
                <div className="text-[11px] text-white/60">
                  {fields.length} field{fields.length === 1 ? "" : "s"}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                  <span className="rounded-full border border-cyan-300/30 bg-cyan-500/15 px-2 py-1 font-semibold text-cyan-50">
                    {docControl.revisionLabel}
                  </span>
                  <span className="rounded-full border border-white/15 bg-white/10 px-2 py-1 text-white/75">
                    {translateMobileEditLabel(docControl.mobileEditLabel)}
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-end text-[10px] text-white/60 leading-tight">
                {form.createdAt && (
                  <span title={formatTimestamp(form.createdAt)?.fullDate}>
                    Created: {formatTimestamp(form.createdAt)?.relative}
                  </span>
                )}
                {form.updatedAt && (
                  <span title={formatTimestamp(form.updatedAt)?.fullDate}>
                    Edited: {formatTimestamp(form.updatedAt)?.relative}
                  </span>
                )}
              </div>
            </div>

            <div className="mb-3 rounded-xl border border-white/10 bg-white/5 p-3 text-[11px] text-white/75">
              <div className="font-semibold uppercase tracking-wider text-white/50 mb-2">
                {t('dashboard.documentControl.snapshotTitle')}
              </div>
              <div className="grid gap-1">
                <div>{t('dashboard.documentControl.type')}: <span className="text-white">{docControl.documentType}</span></div>
                <div>{t('dashboard.documentControl.author')}: <span className="text-white">{docControl.authorName}</span></div>
                <div>{t('dashboard.documentControl.approvalDate')}: <span className="text-white">{docControl.approvedAt ? docControl.approvedAt.toLocaleString() : t('dashboard.documentControl.notRecorded')}</span></div>
                <div>{t('dashboard.documentControl.latestChange')}: <span className="text-white">{docControl.lastChangeSummary}</span></div>
                {docControl.lastComments ? (
                  <div>{t('dashboard.documentControl.comments')}: <span className="text-white">{docControl.lastComments}</span></div>
                ) : null}
              </div>
            </div>

            {/* View modes */}
            {viewMode === "phone" ? (
              <div className="space-y-2">
                <PhonePreview form={form} />
                {/* Per-question attachments under phone preview */}
                {fields.map(
                  (f, i) =>
                    Array.isArray(f.attachments) &&
                    f.attachments.length > 0 && (
                      <div key={i} className={classNames("mt-2", compactGrid ? "mt-1" : "mt-2")}>
                        <div className="text-[11px] font-medium text-white/75 mb-1">
                          {f.label || "Untitled field"}
                        </div>
                        <div className="grid grid-cols-4 gap-1.5">
                          {f.attachments.slice(0, 4).map((att, idx) => (
                            <div
                              key={idx}
                              onClick={() => onPreviewAttachment?.(att)}
                              className="relative border border-white/15 rounded-md overflow-hidden group cursor-pointer bg-white/5"
                              title={att.name}
                            >
                              {att.type?.startsWith("image/") ? (
                                <img
                                  src={att.url}
                                  alt={att.name}
                                  className="w-full h-16 object-cover group-hover:opacity-80 transition-opacity"
                                />
                              ) : att.type === "application/pdf" ? (
                                <div className="flex items-center justify-center h-16 bg-white/10 text-xs text-white/80">
                                  📄 PDF
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-16 bg-white/5 text-xs text-white/75">
                                  📁 File
                                </div>
                              )}
                            </div>
                          ))}
                          {f.attachments.length > 4 && (
                            <div className="flex items-center justify-center h-16 border border-white/15 rounded-md bg-white/10 text-xs text-white/70">
                              +{f.attachments.length - 4} more…
                            </div>
                          )}
                        </div>
                      </div>
                    )
                )}
              </div>
            ) : (
              <div className="text-sm text-white/80 mt-2 space-y-1">
                {fields.length > 0 ? (
                  fields.slice(0, compactGrid ? 6 : 8).map((f, i) => (
                    <div key={i} className="py-1 border-b border-white/10">
                      <div className="flex items-center justify-between text-white">
                        <span className="truncate">{f.label || "Untitled field"}</span>
                        <span className="text-[10px] text-white/60 ml-2">{f.type || "text"}</span>
                      </div>
                      {Array.isArray(f.attachments) && f.attachments.length > 0 && (
                        <div className="mt-1 grid grid-cols-4 gap-1.5">
                          {f.attachments.slice(0, 4).map((att, idx) => (
                            <div
                              key={idx}
                              onClick={() => onPreviewAttachment?.(att)}
                              className="relative border border-white/15 rounded-md overflow-hidden group cursor-pointer bg-white/5"
                              title={att.name}
                            >
                              {att.type?.startsWith("image/") ? (
                                <img
                                  src={att.url}
                                  alt={att.name}
                                  className="w-full h-16 object-cover group-hover:opacity-80 transition-opacity"
                                />
                              ) : att.type === "application/pdf" ? (
                                <div className="flex items-center justify-center h-16 bg-white/10 text-xs text-white/80">
                                  📄 PDF
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-16 bg-white/5 text-xs text-white/75">
                                  📁 File
                                </div>
                              )}
                            </div>
                          ))}
                          {f.attachments.length > 4 && (
                            <div className="flex items-center justify-center h-16 border border-white/15 rounded-md bg-white/10 text-xs text-white/70">
                              +{f.attachments.length - 4} more…
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-white/60 italic">(no fields)</p>
                )}
                {fields.length > (compactGrid ? 6 : 8) && (
                  <div className="text-[11px] text-white/60 mt-1">
                    +{fields.length - (compactGrid ? 6 : 8)} more fields…
                  </div>
                )}
              </div>
            )}

            {/* Unified Attachments Panel (Quick View) */}
            {attachmentsByField.length > 0 && (
              <div className={classNames(
                "mt-3 border border-white/10 rounded-xl bg-white/5 backdrop-blur-md shadow-inner shadow-black/30 p-3",
                compactGrid ? "p-2" : "p-3"
              )}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60 mb-2">
                  Attachments (Quick View)
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {attachmentsByField
                    .flatMap((x) => x.attachments)
                    .slice(0, 4)
                    .map((att, i) => (
                      <div
                        key={i}
                        onClick={() => onPreviewAttachment?.(att)}
                        className="relative border border-white/15 rounded-md overflow-hidden group cursor-pointer bg-white/5"
                        title={att.name}
                      >
                        {att.type?.startsWith("image/") ? (
                          <img
                            src={att.url}
                            alt={att.name}
                            className="w-full h-16 object-cover group-hover:opacity-80 transition-opacity"
                          />
                        ) : att.type === "application/pdf" ? (
                          <div className="flex items-center justify-center h-16 bg-white/10 text-xs text-white/80">
                            📄 PDF
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-16 bg-white/5 text-xs text-white/75">
                            📁 File
                          </div>
                        )}
                      </div>
                    ))}
                  {attachmentsByField.flatMap((x) => x.attachments).length > 4 && (
                    <div className="flex items-center justify-center h-16 border border-white/15 rounded-md bg-white/10 text-xs text-white/70">
                      +{attachmentsByField.flatMap((x) => x.attachments).length - 4} more…
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Folder Assignment */}
            {showFolderActions && !archived && (
              <div className="mt-3 border border-white/10 rounded-xl bg-white/5 backdrop-blur-md shadow-inner shadow-black/30 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60 mb-2">
                  Move to Folder
                </div>
                <select
                  value={form.folderId || ""}
                  onChange={(e) => onMoveToFolder?.(form.id, e.target.value || null)}
                  className="w-full text-xs bg-white/10 border border-white/20 rounded px-2 py-1 text-white"
                >
                  <option value="">📋 Unfiled</option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      📁 {folder.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <CardActions
              onEdit={() => onEdit?.(form)}
              onPreview={() => onPreview?.(form)}
              onViewAuditTrail={() => onViewAuditTrail?.(form)}
              onViewReports={() => onViewReports?.(form)}
              onDuplicate={() => onDuplicate?.(form)}
              onArchive={() => onArchive?.(form)}
              onUnarchive={() => onUnarchive?.(form)}
              onDelete={() => onDelete?.(form)}
              onManageAccess={() => onManageAccess?.(form)}
              archived={archived}
            />
          </motion.div>
        );
      })}
    </motion.div>
  );
}
